import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  FeedbackDTO,
  FeedbackPriority,
  FeedbackStatus,
  FeedbackType,
} from '@nexply/api-client';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Input,
  Label,
  PageHeader,
  SegmentedControl,
  Select,
  Sheet,
  Textarea,
  cn,
  useToast,
} from '@nexply/ui';
import { Bug, Lightbulb, Sparkles, Trash2, TrendingUp } from 'lucide-react';
import { api } from '../../lib/api.js';
import {
  FEEDBACK_STATUS_LABEL,
  FEEDBACK_STATUS_TONE,
  FEEDBACK_TYPE_LABEL,
} from '../../components/FeedbackWidget.js';

const STATUS_ORDER: FeedbackStatus[] = ['OPEN', 'PLANNED', 'IN_PROGRESS', 'DONE', 'REJECTED'];
const PRIORITIES: FeedbackPriority[] = ['LOW', 'MEDIUM', 'HIGH'];

const PRIORITY_LABEL: Record<FeedbackPriority, string> = {
  LOW: 'Niedrig',
  MEDIUM: 'Mittel',
  HIGH: 'Hoch',
};

const PRIORITY_TONE: Record<FeedbackPriority, 'neutral' | 'warn' | 'red'> = {
  LOW: 'neutral',
  MEDIUM: 'warn',
  HIGH: 'red',
};

const TYPE_ICON: Record<FeedbackType, typeof Bug> = {
  BUG: Bug,
  FEATURE: Sparkles,
  IMPROVEMENT: TrendingUp,
  OTHER: Lightbulb,
};

export function AdminFeedbackPage() {
  const [status, setStatus] = useState<FeedbackStatus | 'ALL'>('ALL');
  const [type, setType] = useState<FeedbackType | 'ALL'>('ALL');
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);

  const stats = useQuery({
    queryKey: ['admin', 'feedback', 'stats'],
    queryFn: () => api.feedback.adminStats(),
  });

  const list = useQuery({
    queryKey: ['admin', 'feedback', status, type, q],
    queryFn: () =>
      api.feedback.adminList({
        ...(status === 'ALL' ? {} : { status }),
        ...(type === 'ALL' ? {} : { type }),
        ...(q.trim() ? { q: q.trim() } : {}),
      }),
  });

  const grouped = useMemo(() => {
    const rows = list.data?.feedback ?? [];
    return STATUS_ORDER.map((s) => ({ status: s, rows: rows.filter((r) => r.status === s) })).filter(
      (g) => g.rows.length > 0,
    );
  }, [list.data]);

  return (
    <div className="space-y-6 max-w-5xl">
      <PageHeader
        title="Feedback"
        subtitle="Alles, was Nutzer über den Feedback-Button eingereicht haben."
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Gesamt" value={stats.data?.total ?? '—'} />
        <Kpi label="Offen" value={stats.data?.open ?? '—'} accent />
        <Kpi label="In Arbeit" value={stats.data?.inProgress ?? '—'} />
        <Kpi label="Erledigt" value={stats.data?.done ?? '—'} />
      </div>

      <Card>
        <CardBody className="space-y-3">
          <SegmentedControl
            size="sm"
            value={status}
            onChange={(v) => setStatus(v as FeedbackStatus | 'ALL')}
            options={[
              { value: 'ALL', label: 'Alle' },
              ...STATUS_ORDER.map((s) => ({ value: s, label: FEEDBACK_STATUS_LABEL[s] })),
            ]}
            className="w-full"
          />
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_200px] gap-3">
            <Input
              value={q}
              placeholder="Suche in Titel, Text oder E-Mail …"
              onChange={(e) => setQ(e.currentTarget.value)}
            />
            <Select value={type} onChange={(e) => setType(e.currentTarget.value as FeedbackType | 'ALL')}>
              <option value="ALL">Alle Typen</option>
              {(Object.keys(FEEDBACK_TYPE_LABEL) as FeedbackType[]).map((t) => (
                <option key={t} value={t}>
                  {FEEDBACK_TYPE_LABEL[t]}
                </option>
              ))}
            </Select>
          </div>
        </CardBody>
      </Card>

      {list.isLoading && <div className="text-sm text-text-muted">Lädt…</div>}

      {list.data && grouped.length === 0 && (
        <div className="rounded-2xl border border-dashed border-white/[0.08] py-14 text-center">
          <div className="text-sm text-text-dim">Kein Feedback gefunden.</div>
        </div>
      )}

      {grouped.map((g) => (
        <div key={g.status} className="space-y-2.5">
          <div className="flex items-center gap-2">
            <Badge tone={FEEDBACK_STATUS_TONE[g.status]}>{FEEDBACK_STATUS_LABEL[g.status]}</Badge>
            <span className="text-xs text-text-muted">{g.rows.length}</span>
          </div>
          {g.rows.map((f) => (
            <FeedbackRow key={f.id} f={f} onOpen={() => setOpenId(f.id)} />
          ))}
        </div>
      ))}

      <FeedbackDetailSheet id={openId} onClose={() => setOpenId(null)} />
    </div>
  );
}

function Kpi({ label, value, accent }: { label: string; value: number | string; accent?: boolean }) {
  return (
    <div
      className={cn(
        'rounded-xl border px-3 py-2.5',
        accent ? 'border-gold/25 bg-gold/[0.06]' : 'border-white/[0.06] bg-white/[0.02]',
      )}
    >
      <div className="text-[10px] uppercase tracking-wider text-text-muted">{label}</div>
      <div className={cn('mt-1 text-xl font-semibold tabular-nums', accent ? 'text-gold-light' : 'text-text')}>
        {value}
      </div>
    </div>
  );
}

function FeedbackRow({ f, onOpen }: { f: FeedbackDTO; onOpen: () => void }) {
  const Icon = TYPE_ICON[f.type];
  return (
    <button
      onClick={onOpen}
      className="w-full text-left rounded-xl border border-white/[0.06] bg-surface-2 p-3.5 hover:border-gold/25 hover:bg-surface-3 transition"
    >
      <div className="flex items-start gap-3">
        <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-white/[0.04] text-text-muted">
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="text-sm font-medium text-text truncate">{f.title}</div>
            <Badge tone={PRIORITY_TONE[f.priority]}>{PRIORITY_LABEL[f.priority]}</Badge>
          </div>
          <div className="mt-0.5 text-[11px] text-text-muted truncate">
            {FEEDBACK_TYPE_LABEL[f.type]} · {f.authorName || f.authorEmail || 'Unbekannt'} ·{' '}
            {new Date(f.createdAt).toLocaleDateString('de-DE')}
            {f.hasScreenshot && ' · 📎'}
          </div>
          {f.body && <p className="mt-1.5 text-xs text-text-dim line-clamp-2">{f.body}</p>}
        </div>
      </div>
    </button>
  );
}

function FeedbackDetailSheet({ id, onClose }: { id: string | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'feedback', 'detail', id],
    queryFn: () => api.feedback.get(id as string),
    enabled: !!id,
  });

  const f = data?.feedback;
  const [note, setNote] = useState<string | null>(null);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['admin', 'feedback'] });
  };

  const update = useMutation({
    mutationFn: (input: Parameters<typeof api.feedback.adminUpdate>[1]) =>
      api.feedback.adminUpdate(id as string, input),
    onSuccess: () => {
      toast.success('Gespeichert.');
      invalidate();
    },
    onError: () => toast.error('Speichern fehlgeschlagen.'),
  });

  const remove = useMutation({
    mutationFn: () => api.feedback.remove(id as string),
    onSuccess: () => {
      toast.success('Gelöscht.');
      invalidate();
      onClose();
    },
    onError: () => toast.error('Löschen fehlgeschlagen.'),
  });

  const noteValue = note ?? f?.adminNote ?? '';

  return (
    <Sheet
      open={!!id}
      onClose={() => {
        setNote(null);
        onClose();
      }}
      title={f?.title ?? 'Feedback'}
      description={
        f
          ? `${FEEDBACK_TYPE_LABEL[f.type]} · ${f.authorName || f.authorEmail || 'Unbekannt'} · ${new Date(
              f.createdAt,
            ).toLocaleString('de-DE')}`
          : undefined
      }
      size="lg"
    >
      {isLoading && <div className="text-sm text-text-muted py-6 text-center">Lädt…</div>}
      {f && (
        <div className="space-y-5">
          {f.body && (
            <div className="rounded-xl border border-white/[0.06] bg-surface-2 p-3.5 text-sm text-text-dim whitespace-pre-wrap">
              {f.body}
            </div>
          )}

          {f.pageUrl && (
            <div className="text-[11px] text-text-muted">
              Gemeldet auf <span className="text-text-dim">{f.pageUrl}</span>
            </div>
          )}

          {f.screenshotDataUrl && (
            <a href={f.screenshotDataUrl} target="_blank" rel="noreferrer" className="block">
              <img
                src={f.screenshotDataUrl}
                alt="Screenshot"
                className="w-full rounded-xl border border-white/[0.08] bg-black/40 object-contain max-h-80"
              />
            </a>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Status</Label>
              <Select
                value={f.status}
                disabled={update.isPending}
                onChange={(e) => update.mutate({ status: e.currentTarget.value as FeedbackStatus })}
              >
                {STATUS_ORDER.map((s) => (
                  <option key={s} value={s}>
                    {FEEDBACK_STATUS_LABEL[s]}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <Label>Priorität</Label>
              <Select
                value={f.priority}
                disabled={update.isPending}
                onChange={(e) =>
                  update.mutate({ priority: e.currentTarget.value as FeedbackPriority })
                }
              >
                {PRIORITIES.map((p) => (
                  <option key={p} value={p}>
                    {PRIORITY_LABEL[p]}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="fb-admin-note">Interne Notiz / Antwort an den Melder</Label>
            <Textarea
              id="fb-admin-note"
              rows={4}
              maxLength={5000}
              value={noteValue}
              placeholder="Wird dem Melder in seinen Vorschlägen angezeigt."
              onChange={(e) => setNote(e.currentTarget.value)}
            />
            <div className="mt-2 flex justify-end">
              <Button
                size="sm"
                disabled={update.isPending || noteValue === (f.adminNote ?? '')}
                onClick={() => update.mutate({ adminNote: noteValue })}
              >
                Notiz speichern
              </Button>
            </div>
          </div>

          <div className="border-t border-white/[0.06] pt-4">
            <Button
              size="sm"
              variant="danger"
              disabled={remove.isPending}
              onClick={() => {
                if (confirm('Dieses Feedback endgültig löschen?')) remove.mutate();
              }}
            >
              <Trash2 className="h-4 w-4" />
              Löschen
            </Button>
          </div>
        </div>
      )}
    </Sheet>
  );
}
